import { useCallback, useEffect, useRef, useState } from 'react';

export type BotValidatedState = 'idle' | 'pending' | 'success' | 'rejected' | 'timeout';

export interface UseBotValidatedActionOptions<TResult> {
  action: () => Promise<TResult>;
  onSuccess?: (result: TResult) => void;
  onReject?: () => void;
  timeoutMs: number;
  timeoutIsSuccess?: boolean;
}

export interface UseBotValidatedActionReturn {
  state: BotValidatedState;
  execute: () => Promise<void>;
  reset: () => void;
}

const useBotValidatedAction = <TResult>({
  action,
  onSuccess,
  onReject,
  timeoutMs,
  timeoutIsSuccess = false,
}: UseBotValidatedActionOptions<TResult>): UseBotValidatedActionReturn => {
  const [state, setState] = useState<BotValidatedState>('idle');

  const stateRef = useRef<BotValidatedState>('idle');
  const isMountedRef = useRef(true);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSuccessRef = useRef(onSuccess);
  const onRejectRef = useRef(onReject);
  const timeoutIsSuccessRef = useRef(timeoutIsSuccess);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onRejectRef.current = onReject;
    timeoutIsSuccessRef.current = timeoutIsSuccess;
  }, [onSuccess, onReject, timeoutIsSuccess]);

  useEffect(
    () => () => {
      isMountedRef.current = false;
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    },
    []
  );

  const readState = useCallback((): BotValidatedState => stateRef.current, []);

  const writeState = useCallback((next: BotValidatedState) => {
    stateRef.current = next;
    if (isMountedRef.current) setState(next);
  }, []);

  const clearPendingTimeout = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    clearPendingTimeout();
    writeState('idle');
  }, [clearPendingTimeout, writeState]);

  const execute = useCallback(async () => {
    if (readState() === 'pending') return;

    writeState('pending');

    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      if (readState() !== 'pending' || !isMountedRef.current) return;

      if (timeoutIsSuccessRef.current) {
        writeState('success');
        onSuccessRef.current?.(undefined as unknown as TResult);
      } else {
        writeState('timeout');
      }
    }, timeoutMs);

    try {
      const result = await action();

      if (readState() !== 'pending' || !isMountedRef.current) return;

      clearPendingTimeout();
      writeState('success');
      onSuccessRef.current?.(result);
    } catch {
      if (readState() !== 'pending' || !isMountedRef.current) return;

      clearPendingTimeout();
      writeState('rejected');
      onRejectRef.current?.();
    }
  }, [action, clearPendingTimeout, readState, timeoutMs, writeState]);

  return { state, execute, reset };
};

export default useBotValidatedAction;
